import { useMemo, useState, type JSX } from "react";
import type { Difficulty, GenerateCasesRequest, TaxonomySectorDto } from "../api/types";

interface AdminGeneratePanelProps {
  taxonomy: readonly TaxonomySectorDto[];
  isBusy: boolean;
  message: string | null;
  onGenerate: (request: GenerateCasesRequest) => Promise<void>;
}

const MAX_CASE_COUNT = 1000;

function findSharedTopics(taxonomy: readonly TaxonomySectorDto[]): TaxonomySectorDto["topics"] {
  const [firstSector, ...remainingSectors] = taxonomy;
  if (firstSector === undefined)
    return [];

  return firstSector.topics.filter((topic) =>
    remainingSectors.every((sector) =>
      sector.topics.some((candidate) => candidate.slug === topic.slug)));
}

/**
 * Lets an administrator configure grounded case generation into the review queue.
 */
export function AdminGeneratePanel({
  taxonomy,
  isBusy,
  message,
  onGenerate,
}: AdminGeneratePanelProps): JSX.Element {
  const [difficulty, setDifficulty] = useState<Difficulty>("Easy");
  const [countInput, setCountInput] = useState("1");
  const [sector, setSector] = useState("");
  const [topic, setTopic] = useState("");
  const [sourceQuery, setSourceQuery] = useState("");

  const selectedSector = taxonomy.find((item) => item.slug === sector) ?? null;
  const topics = useMemo(
    () => selectedSector?.topics ?? findSharedTopics(taxonomy),
    [selectedSector, taxonomy],
  );
  const count = Number(countInput);
  const isCountValid = Number.isInteger(count) && count >= 1 && count <= MAX_CASE_COUNT;
  const sectorNames = taxonomy.map((item) => item.name).join(", ").replace(/, ([^,]*)$/, " og $1");

  const setDifficultyValue = (value: string): void => {
    if (value === "Easy" || value === "Medium" || value === "Hard") {
      setDifficulty(value);
      return;
    }

    throw new Error(`Unhandled difficulty: ${value}`);
  };

  const submit = (): void => {
    if (!isCountValid)
      return;

    void onGenerate({
      difficulty,
      count,
      category: topic.length > 0 ? topic : null,
      sectors: sector.length > 0 ? [sector] : null,
      sourceQuery: sourceQuery.trim().length > 0 ? sourceQuery.trim() : null,
    });
  };

  return (
    <section className="review-card generate-panel">
      <header className="panel-heading">
        <div>
          <p className="eyebrow">Caseverksted</p>
          <h2>Lag nye caser</h2>
          <p className="panel-description">
            Velg innhold og omfang. Casene lagres i kontrollkøen og blir ikke synlige
            i spillet før de er godkjent.
          </p>
        </div>
        <span className="model-badge">GPT-5.6 Terra</span>
      </header>

      <div className="generate-form">
        <label className="field" htmlFor="generate-sector">
          <span>Fagområde</span>
          <select
            id="generate-sector"
            aria-label="Fagområde"
            value={sector}
            disabled={isBusy}
            onChange={(event) => {
              setSector(event.target.value);
              setTopic("");
            }}
          >
            <option value="">Fordel på alle fagområder</option>
            {taxonomy.map((item) => (
              <option key={item.slug} value={item.slug}>{item.name}</option>
            ))}
          </select>
          <small>
            {selectedSector === null
              ? `Casene fordeles på ${sectorNames}.`
              : `Alle casene lages for ${selectedSector.name}.`}
          </small>
        </label>

        <label className="field" htmlFor="generate-topic">
          <span>Tema</span>
          <select
            id="generate-topic"
            aria-label="Tema"
            value={topic}
            disabled={isBusy}
            onChange={(event) => { setTopic(event.target.value); }}
          >
            <option value="">
              {selectedSector === null ? "Varier mellom relevante tema" : "Varier mellom alle tema"}
            </option>
            {topics.map((item) => (
              <option key={item.slug} value={item.slug}>{item.name}</option>
            ))}
          </select>
          <small>
            Tverrgående tema er tilgjengelige når alle fagområder er valgt.
          </small>
        </label>

        <label className="field" htmlFor="generate-difficulty">
          <span>Vanskelighetsgrad</span>
          <select
            id="generate-difficulty"
            aria-label="Vanskelighetsgrad"
            value={difficulty}
            disabled={isBusy}
            onChange={(event) => { setDifficultyValue(event.target.value); }}
          >
            <option value="Easy">Lett</option>
            <option value="Medium">Middels</option>
            <option value="Hard">Krevende</option>
          </select>
          <small>Styrer hvor komplekst resonnement spilleren må gjøre.</small>
        </label>

        <label className="field" htmlFor="generate-count">
          <span>Antall caser</span>
          <input
            id="generate-count"
            aria-label="Antall caser"
            type="number"
            min={1}
            max={MAX_CASE_COUNT}
            step={1}
            value={countInput}
            disabled={isBusy}
            aria-invalid={!isCountValid}
            aria-describedby={!isCountValid ? "generate-count-error" : "generate-count-help"}
            onChange={(event) => { setCountInput(event.target.value); }}
          />
          {isCountValid
            ? <small id="generate-count-help">Fra 1 til 1000. Store batcher kjører i bakgrunnen.</small>
            : <small id="generate-count-error" className="field-error">Skriv inn et antall fra 1 til 1000.</small>}
        </label>

        <label className="field field-wide" htmlFor="generate-source-query">
          <span>Kildesøk <em>valgfritt</em></span>
          <input
            id="generate-source-query"
            type="text"
            maxLength={200}
            value={sourceQuery}
            disabled={isBusy}
            placeholder="For eksempel: omplassering før oppsigelse"
            onChange={(event) => { setSourceQuery(event.target.value); }}
          />
          <small>
            Styr hvilke Veilederen-kilder modellen skal bruke. Kildene fryses og vises i kontrollen.
          </small>
        </label>
      </div>

      <footer className="generate-actions">
        <div>
          <strong>{isCountValid ? count.toLocaleString("nb-NO") : "–"} caser</strong>
          <span>{sector.length === 0 ? " fordelt på alle fagområder" : ` for ${selectedSector?.name ?? sector}`}</span>
        </div>
        <button type="button" disabled={isBusy || !isCountValid} onClick={submit}>
          {isBusy ? "Starter …" : "Start generering"}
        </button>
      </footer>

      {message !== null && <p className="generation-status" role="status">{message}</p>}
    </section>
  );
}
